import React from "react";
import { Routes, BrowserRouter, Route, useLocation } from "react-router-dom";

import * as Material from "@mui/material";

import { Confirm } from "@fragments";
import UserModal from "@components/Modals/UserModal";
import ShareContentModal from "@components/Modals/ShareContentModal/index.js";

import { AuthProvider } from "@contexts/AuthContext.js";
import { CommunityProvider } from "contexts/community";

import { ThemeProvider } from "@mui/material/styles";

import theme from "./assets/theme.js";

import H from "@helpers/H.js";

import { ScreenProvider, useScreen as useLegacyScreen } from "@hooks/useScreen";

import routes from "./routes";
import { isPublicPath } from "@helpers/publicRoutes.js";
import MentoringInvite from "@pages/Mentoring/invite";

import "./App.css";
import "./assets/spacing.css";
import Screen from "Screen/index.js";

function App() {
  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <AuthProvider>
          <CommunityProvider>
            <ScreenProvider>
              <AppRoutes />
            </ScreenProvider>
          </CommunityProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

// Top-level route switch (§10). Public paths render their own bare <Routes> with
// no app shell (no Sidebar/Header) — the AuthProvider already skips the auth
// redirect for them, so logged-out visitors reach them. Everything else falls
// through to the authenticated, shell-wrapped ProtectedRoutes tree. Only one
// <Routes> is ever mounted at a time, so the protected routes' absolute paths
// are never affected by the public branch.
function AppRoutes() {
  const location = useLocation();

  if (isPublicPath(location.pathname)) {
    return (
      <>
        <SnackbarHost />
        <Confirm />
        <Routes>
          <Route path="/mentoring/invite/:token" element={<MentoringInvite />} />
        </Routes>
      </>
    );
  }

  return <ProtectedRoutes />;
}

function ScreenLayers({ children }) {
  return (
    <Screen>
      {children}
    </Screen>
  )
}

function SnackbarHost() {
  const { snackbar, setSnackbar } = useLegacyScreen();
  if (!snackbar) return null;
  return (
    <Material.Portal>
      <Material.Snackbar
        className="custom-snackbar"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={!!snackbar}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        TransitionComponent={Material.Fade}
        TransitionProps={{ timeout: { enter: 0, exit: 200 } }}
        sx={{ zIndex: 9999 }}
      />
    </Material.Portal>
  );
}

const ProtectedRoutes = () => {
  window.helpers = H;

  return (
    <>
      <SnackbarHost />
      <ScreenLayers>
        <Routes>
          {Object.keys(routes).map((path, i) => {
            const Component = routes[path];
            return (
              <Route
                path={"/" + path}
                key={i}
                element={
                  <>
                    <Confirm />
                    <UserModal />
                    <Component />
                    <ShareContentModal />
                  </>
                }
              />
            );
          })}
        </Routes>
      </ScreenLayers>
    </>
  );
};

export default App;
